import { type PastDuePolicy, type ReminderSettings, type ReminderSettingsUpdate } from '../../../../models/Scheduling'

export const MAX_REMINDER_ENTRIES = 4

export interface ReminderRowDraft {
  key: string
  id: string | null
  offsetMinutes: number
}

export interface ReminderSettingsDraft {
  enabled: boolean
  rows: ReminderRowDraft[]
}

export const formatReminderOffset = (offsetMinutes: number): string => {
  const magnitude = Math.abs(offsetMinutes)
  if (offsetMinutes < 0 && magnitude <= 2880) {
    const hours = magnitude / 60
    return hours === 1 ? '1 hour' : `${hours} hours`
  }
  if (offsetMinutes < 0 && magnitude % 10080 === 0) {
    const weeks = magnitude / 10080
    return weeks === 1 ? '1 week' : `${weeks} weeks`
  }
  const days = magnitude / 1440
  return days === 1 ? '1 day' : `${days} days`
}

export const toSettingsDraft = (settings: ReminderSettings): ReminderSettingsDraft => ({
  enabled: settings.enabled,
  rows: settings.entries.map((entry) => ({ key: entry.id ?? `default-${entry.offsetMinutes}`, id: entry.id, offsetMinutes: entry.offsetMinutes })),
})

export const nextReminderOffset = (allowedOffsets: number[], rows: ReminderRowDraft[]): number => {
  const used = rows.map((row) => row.offsetMinutes)
  const lastUsedIndex = used.reduce((last, offset) => Math.max(last, allowedOffsets.indexOf(offset)), -1)
  const following = allowedOffsets.slice(lastUsedIndex + 1).find((offset) => !used.includes(offset))
  return following ?? allowedOffsets.find((offset) => !used.includes(offset)) ?? allowedOffsets[0]
}

export const createRowDraft = (offsetMinutes: number): ReminderRowDraft => ({ key: crypto.randomUUID(), id: null, offsetMinutes })

export const toSettingsUpdate = (draft: ReminderSettingsDraft, pastDuePolicy: PastDuePolicy): ReminderSettingsUpdate => ({
  enabled: draft.enabled,
  entries: draft.rows.map((row) => (row.id == null ? { offsetMinutes: row.offsetMinutes } : { id: row.id, offsetMinutes: row.offsetMinutes })),
  pastDuePolicy,
})

export const entriesChanged = (draft: ReminderSettingsDraft, settings: ReminderSettings): boolean =>
  draft.rows.length !== settings.entries.length ||
  draft.rows.some((row, index) => row.id !== settings.entries[index].id || row.offsetMinutes !== settings.entries[index].offsetMinutes)

export const settingsChanged = (draft: ReminderSettingsDraft, settings: ReminderSettings): boolean =>
  draft.enabled !== settings.enabled || entriesChanged(draft, settings)

export const hasEntryMovedEarlier = (draft: ReminderSettingsDraft, settings: ReminderSettings): boolean =>
  draft.rows.some((row) => {
    if (row.id == null) {
      return false
    }
    const entry = settings.entries.find((candidate) => candidate.id === row.id)
    return entry != null && row.offsetMinutes < entry.offsetMinutes
  })
